import { useMemo } from 'react'
import {
  currentFiscalYear,
  type BudgetCode,
  type NamedCodeLike,
} from '@budgetmonkey/shared'
import { BudgetCodeForm } from './BudgetCodeForm'
import { ExternalCodeForm } from './ExternalCodeForm'
import {
  useBudgetCodes,
  useUpdateBudgetCode,
  useUpdateExternalBudgetCode,
} from './hooks'

/**
 * Edit one budget code, whichever kind it is.
 *
 * The two forms are genuinely different — an external code has an owner's name
 * and no fund, category or beginning balance — so choosing between them is a
 * decision, and one made in more than one place: the codes list and the code
 * detail page both edit. Having it here means neither can drift into opening
 * the wrong form and writing a malformed code.
 */
export function BudgetCodeEditor({
  code,
  fiscalYear = currentFiscalYear(),
  onDone,
}: {
  code: BudgetCode
  fiscalYear?: number
  onDone: () => void
}) {
  const { data: codes = [] } = useBudgetCodes(fiscalYear)
  const update = useUpdateBudgetCode(fiscalYear)
  const updateExternal = useUpdateExternalBudgetCode(fiscalYear)

  // Every hook above any branch: this component returns one of two forms, and
  // hooks called after a conditional return are React error #300.
  const existingCodes = useMemo<NamedCodeLike[]>(
    () =>
      codes.map((c) => ({
        id: c.id,
        code: c.code,
        aliases: c.aliases,
        displayName: c.displayName,
      })),
    [codes],
  )

  if (code.external) {
    return (
      <ExternalCodeForm
        initial={code}
        currentCodeId={code.id}
        existingCodes={existingCodes}
        submitLabel="Save changes"
        pending={updateExternal.isPending}
        error={updateExternal.error?.message}
        onCancel={onDone}
        onSubmit={(values) =>
          updateExternal.mutate({ id: code.id!, input: values }, { onSuccess: onDone })
        }
      />
    )
  }

  return (
    <BudgetCodeForm
      initial={code}
      currentCodeId={code.id}
      existingCodes={existingCodes}
      submitLabel="Save changes"
      pending={update.isPending}
      error={update.error?.message}
      onCancel={onDone}
      onSubmit={(values) =>
        update.mutate({ id: code.id!, patch: values }, { onSuccess: onDone })
      }
    />
  )
}

/** What to call a code on screen — an external one goes by its owner. */
export function budgetCodeTitle(code: BudgetCode): string {
  return code.external ? (code.externalOwnerName ?? code.shortLabel) : code.displayName
}
